import { useRouter } from "next/router";
import { ReactElement, useCallback } from "react";
import { useHeaderNavLinkStyles } from "./HeaderNav.styled";
import {
  Burger,
  Flex,
  Header,
  MediaQuery,
  NavLink,
  useMantineTheme,
} from "@mantine/core";
import { IHeaderNavProps } from "./types";
import Link from "next/link";
import { SECTION_BILLING_ADDRESS } from "@pages/account/manage/billing";
import { purchasesStore } from "@stores/purchases";
import { HeaderNavBanner, HeaderNavLogo } from "./HeaderNav.components";
import { pluralize } from "@common/utils/string";
import { sessionStore } from "@stores/session";
import { useAccount } from "@stores/session/hooks";
import { isEnterprisePlan } from "@common/utils/plan";
import { redirectAfterLogout } from "@common/utils/router";
import { useIsPlanValid } from "@stores/plans/hooks";
import { useGetHeaderProperties } from "./hooks";

export default function HeaderNav({
  opened,
  hideMenu,
  onBurgerClick,
}: IHeaderNavProps): ReactElement {
  const theme = useMantineTheme();
  const { classes: headerNavLinkStyles } = useHeaderNavLinkStyles();

  const router = useRouter();
  const { pathname } = router;

  // purchases
  const { data: purchases } = purchasesStore.useGetPurchases();

  // session
  const account = useAccount();
  const { refetch: getAccount } = sessionStore.useGetAccount({
    enabled: false,
  });

  // logout
  const { mutate: logout } = sessionStore.useLogout();
  const logoutCallback = useCallback(() => {
    logout(null, {
      onSuccess() {
        redirectAfterLogout(router);
      },
    });
  }, [logout, router]);

  // are we in one of the checkout pages? `/checkout`
  const showAccountLink = !!account;

  // purchase in trial
  const purchaseInTrial = purchases
    ?.filter(({ product }) => !isEnterprisePlan(product.plan.alias))
    .find(({ subscription }) => subscription.in_trial);
  const purchaseInTrialHasExpired = !!(
    purchaseInTrial &&
    typeof purchaseInTrial.subscription.trial_end_days === "number" &&
    purchaseInTrial.subscription.trial_end_days < 0
  );

  // plan
  const isValidPurchaseInTrialPlan = useIsPlanValid(
    purchaseInTrial?.product.plan.alias || "",
  );

  // banners
  const { hasTrialBanner, hasBillingAddressBanner, headerHeight } =
    useGetHeaderProperties();

  return (
    <Header id="main-header" height={headerHeight}>
      {hasTrialBanner ? (
        <HeaderNavBanner
          text={
            purchaseInTrialHasExpired
              ? "Your trial has expired."
              : purchaseInTrial?.subscription.trial_end_days
                ? `${purchaseInTrial.subscription.trial_end_days} ${pluralize("day", purchaseInTrial.subscription.trial_end_days)} left in your trial.`
                : "Your trial ends soon."
          }
          href={
            pathname === "/account/manage/plans" || !isValidPurchaseInTrialPlan
              ? undefined
              : "/account/manage/plans"
          }
          hrefText={
            purchaseInTrialHasExpired ? "Manage your subscription" : "Buy now"
          }
        />
      ) : (
        hasBillingAddressBanner && (
          <HeaderNavBanner
            text="Looks like we don't have a billing address."
            href={`/account/manage/billing#section=${SECTION_BILLING_ADDRESS}`}
            hrefText="Add a billing address"
          />
        )
      )}

      {!hideMenu && (
        <Flex
          align="center"
          direction="row"
          justify="space-between"
          wrap="nowrap"
        >
          <HeaderNavLogo account={account} />

          {account && (
            <>
              <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
                <Flex
                  align="baseline"
                  direction="row"
                  justify="space-between"
                  pr="lg"
                  wrap="nowrap"
                >
                  {showAccountLink && (
                    <>
                      <NavLink
                        component={Link}
                        className={`${headerNavLinkStyles.root} ${headerNavLinkStyles.main}`}
                        label={`Logged in as ${account.first_name}`}
                        href="/account/manage"
                        onClick={() => getAccount()}
                      />
                      <NavLink
                        data-testid="nav-link-logout"
                        className={`${headerNavLinkStyles.root} ${headerNavLinkStyles.main}`}
                        label="Logout"
                        icon={undefined}
                        onClick={logoutCallback}
                      />
                    </>
                  )}
                </Flex>
              </MediaQuery>

              <MediaQuery largerThan="sm" styles={{ display: "none" }}>
                <Burger
                  opened={opened}
                  onClick={onBurgerClick}
                  size="sm"
                  color={theme.colors.gray[7]}
                  mr="xl"
                />
              </MediaQuery>
            </>
          )}
        </Flex>
      )}
    </Header>
  );
}
